import React, { useEffect } from 'react';
import { setStoryData } from '../../../../store/storySlice';
import {
  getOrderedReadingLevelKeys,
  StoryAgeTitleEnum,
  StoryTypeByAgeEnum,
} from '../../../../interfaces/book.interface';
import { DropdownArrow } from '../../../../components/Common/DropdownArrow';
import { AppDispatch } from '../../../../store/store';
import { useDispatch } from 'react-redux';
import { ArtStyleModel } from '../../../../interfaces/story.interface';
import BackButton from '../../../../components/Common/BackButton';
import { SecureImg } from '../../../../components/Common/SecureImage';
import Loader from '../../../../components/Common/Loader';

export interface StepStoryMainInformationProps {
  childName: string;
  childAge: string;
  artStyle: ArtStyleModel | any;
  setChildName: (name: string) => void;
  setChildAge: (age: string) => void;
  setArtStyle: (style: string) => void;
  storyconfig: Record<string, any>;
  setSelectedStoryConfig: (selected: string) => void;
  setShowDescription: (description: StoryTypeByAgeEnum | boolean) => void;
  submitLoader: boolean;
  artStyles: ArtStyleModel[];
  onContinue: () => void;
  onBack?: () => void;
}

const StepStoryMainInformation = ({
  childName,
  childAge,
  artStyle,
  setChildAge,
  setChildName,
  setArtStyle,
  setSelectedStoryConfig,
  storyconfig,
  setShowDescription,
  artStyles,
  onContinue,
  onBack,
                                    submitLoader,
}: StepStoryMainInformationProps) => {
  const dispatch: AppDispatch = useDispatch();

  const isValidForm = () => {
    return childName.trim() !== '' && childAge.trim() !== '' && !!storyconfig[childAge] && !!artStyle;
  };

  return (
    <div className="flex flex-col w-full max-w-2xl mx-auto">
      <div className=" w-full flex justify-between ">
        <div className="flex items-center align-end mb-4">
          <h2 className="text-left text-2xl md:text-4xl font-bold text-storyblue ">Lets Build Your Story!</h2>
        </div>
      </div>

      <p className="mb-4 lg:mb-8 text-left text-sm text-storyblue">How would you like your story to look?</p>
      <div className="grid grid-cols-1 gap-4 lg:gap-8">
        <div className="col-span-2 md:col-span-1">
          <label className="text-left block text-sm font-medium leading-6 text-secondaryStoryBlackText">Main Character's Name:</label>
          <input
            type="text"
            className="w-full rounded-md text-sm md:text-xl p-2"
            value={childName}
            onChange={(e) => {
              setChildName(e.target.value);
              dispatch(setStoryData({ childName: e.target.value }));
            }}
            autoFocus
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <label className="text-left block text-sm font-medium leading-6 text-secondaryStoryBlackText">Reading Level:</label>
          <div className="grid ">
            <select
              className="row-start-1 col-start-1 w-full rounded-md text-sm md:text-xl p-2 appearance-none"
              value={childAge}
              onChange={(e) => {
                setChildAge(e.target.value);
                dispatch(setStoryData({ childAge: e.target.value }));
                setSelectedStoryConfig(e.target.value);
                setShowDescription(StoryTypeByAgeEnum.CHILD_AGE);
              }}
            >
              <option value="" disabled>
                Please Select Reading Level
              </option>
              {getOrderedReadingLevelKeys(storyconfig).map((optionKey) => (
                <option key={optionKey} value={optionKey}>
                  {StoryAgeTitleEnum[optionKey as keyof typeof StoryAgeTitleEnum] || optionKey}
                  {storyconfig[optionKey]?.words_total ? ` (${storyconfig[optionKey].words_total} words per page)` : ''}
                </option>
              ))}
            </select>
            {DropdownArrow()}
          </div>
          <p className="mt-1 text-xs text-gray-500 text-left">Sets the maximum words allowed per page for writing and AI-generated stories.</p>
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-left block text-sm font-medium leading-6 text-secondaryStoryBlackText">Art Style:</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-8">
            {artStyles?.map((style) => (
              <div
                key={style.id}
                className={`rounded-md cursor-pointer flex flex-col items-center justify-start ${artStyle === style?.id ? 'shadow-md border border-secondaryStoryAdditionalLightBlueText' : ''} p-2 hover:shadow-md transition-colors`}
                onClick={() => {
                  setArtStyle(style.id!);
                  setShowDescription(StoryTypeByAgeEnum.ART_STYLE);
                  dispatch(setStoryData({ artStyle: style as any }));
                }}
              >
                <SecureImg storageSrc={style.sref_image} alt={style.name} className="md:min-h-42 md:min-w-42 h-42 w-42 object-cover rounded-md mb-2" />
                <h3 className="text-sm font-semibold text-secondaryStoryBlackText">{style.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`flex flex-wrap ${onBack ? 'justify-between' : 'justify-end'} gap-2 md:flex-nowrap mt-5`}>
        {onBack && <BackButton id="step-back-button" onClick={() => window.history.back()}></BackButton>}
        <button
          id="step-next-button"
          type="button"
          className={`px-6 py-2 h-9 ${isValidForm() ? '' : 'opacity-50 cursor-not-allowed'} bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center`}
          onClick={() => onContinue()}
          disabled={!isValidForm()}
        >
          Continue
          <Loader loader={submitLoader} />
        </button>
      </div>
    </div>
  );
};

export default StepStoryMainInformation;
